import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, finalize, map, mergeMap, switchMap, tap } from 'rxjs/operators';
import { QueryParamsModel } from 'src/app/core/models/query-params.model';
import { IResponse } from 'src/app/core/interfaces/response.interface';
import { IBenModel } from 'src/app/core/models/ben.model';

import { ErrorService } from 'src/app/services/error.service';
import { ToastService } from 'src/app/services/toast.service';
import { environment } from 'src/environments/environment';
import * as BenActions from '../actions/ben.actions';
import { AppState } from '../app.reducer';

@Injectable()
export class BenEffects {
  constructor(
    public actions$: Actions,
    public http: HttpClient,
    private errorService: ErrorService,
    private store: Store<AppState>
  ) { }
  payloadReload;

  getBens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BenActions.getBens),
      switchMap(({ payload }) => {
        let query = {};
        if (payload?.query) {
          query = {
            ...payload.query,
            filters: JSON.stringify(payload.query.filters),
          };
        }

        this.store.dispatch(
          BenActions.benLoading({ payload: { loading: true } })
        );
        return this.http
          .get<IResponse<IBenModel[]>>(environment.apiUrl + '/bens', {
            params: { ...query },
          })
          .pipe(
            switchMap((responseData) => {
              this.store.dispatch(
                BenActions.benLoading({ payload: { loading: false } })
              );
              return of(
                BenActions.getBensSuccess({
                  payload: {
                    bens: responseData.data,
                    totalCount: responseData.totalCount,
                  },
                })
              );
            }),
            catchError((error) => {
              return of(
                BenActions.benFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );

  getBen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BenActions.getBenById),
      switchMap((payload) => {
        this.store.dispatch(
          BenActions.benLoading({ payload: { loading: true } })
        );

        return (
          this.http
            .get<any>(environment.apiUrl + '/bens/' + payload.payload.id)
            .pipe(
              switchMap((responseData) => {
                this.store.dispatch(
                  BenActions.getBenByIdSuccess({
                    payload: {
                      ben: responseData['data'],
                    },
                  })
                );
                return of(
                  BenActions.benLoading({ payload: { loading: false } })
                );
              }),
              catchError((error) => {
                this.store.dispatch(
                  BenActions.benFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
                return of(
                  BenActions.benLoading({ payload: { loading: false } })
                );
              })
            )
        );
      })
    )
  );

  updateBen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BenActions.updateBen),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BenActions.benLoading({ payload: { loading: true } })
        );

        return this.http
          .put<any>(environment.apiUrl + '/bens/' + payload.ben.id, payload.ben)

          .pipe(
            switchMap(() => {
              this.store.dispatch(BenActions.benUpdated());
              return of(BenActions.benLoading({ payload: { loading: false } }));
            }),
            catchError((error) => {
              if (
                environment.optimisticLockErrors.includes(error.error?.name)
              ) {
                return of(
                  BenActions.benOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              } else {
                return of(
                  BenActions.benFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );

  deleteBen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BenActions.deleteBen),
      switchMap(({ payload }) => {
        this.store.dispatch(
          BenActions.benLoading({ payload: { loading: true } })
        );

        return this.http
          .delete<any>(environment.apiUrl + '/deleteBens/' + payload.id)
          .pipe(
            switchMap(() => {
              this.store.dispatch(BenActions.benDeleted());
              return of(BenActions.benLoading({ payload: { loading: false } }));
            }),
            catchError((error) => {
              if (
                environment.optimisticLockErrors.includes(error.error?.name)
              ) {
                return of(
                  BenActions.benOptimisticLockError({
                    payload: {
                      optimisticLockError: this.errorService.mapError(error),
                    },
                  })
                );
              } else {
                return of(
                  BenActions.benFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              }
            })
          );
      })
    )
  );


  createBen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(BenActions.createBen),
      switchMap(({ payload }) => {
        this.store.dispatch(BenActions.benLoading({ payload: { loading: true } }));
        return this.http.post<any>(`${environment.apiUrl}/createben`, {
          ben: payload.ben,
          id: payload.id,
          rightId: payload.rightId,
        }).pipe(
          map(responseData => {
            return BenActions.benCreated({ ben: responseData.data });
          }),
          catchError((error) => {
            if (environment.optimisticLockErrors.includes(error.error?.name)) {
              return of(
                BenActions.benOptimisticLockError({
                  payload: {
                    optimisticLockError: this.errorService.mapError(error),
                  },
                })
              );
            } else {
              return of(
                BenActions.benFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            }
          }),
          finalize(() => this.store.dispatch(BenActions.benLoading({ payload: { loading: false } })))
        );
      })
    )
  );



}
